import { InMemoryCache } from '@apollo/client';
import { useState, useEffect } from 'react';

export interface CacheCleanupConfig {
  intervalMs: number;
  maxCleanupTime: number;
  batchSize: number;
  throttleMs: number;
  enabled: boolean;
}

export interface CacheHealthStats {
  cacheSize: number;
  totalCleaned: number;
  lastCleanupDuration: number;
  scheduledCleanups: number;
  skippedCleanups: number;
  isRunning: boolean;
  avgCleanupDuration: number;
}

export class PerformanceMonitor {
  private static measurements = new Map<string, number[]>();

  static measure<T>(operation: string, fn: () => T): T {
    const start = performance.now();
    try {
      const result = fn();
      const duration = performance.now() - start;
      this.recordMeasurement(operation, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMeasurement(operation, duration);
      throw error;
    }
  }

  static async measureAsync<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.recordMeasurement(operation, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMeasurement(operation, duration);
      throw error;
    }
  }

  private static recordMeasurement(operation: string, duration: number) {
    const measurements = this.measurements.get(operation) || [];
    measurements.push(duration);

    // Keep only last 100 measurements to prevent memory leaks
    if (measurements.length > 100) {
      measurements.shift();
    }

    this.measurements.set(operation, measurements);

    // Log warnings for slow operations in development
    if (process.env.NODE_ENV === 'development' && duration > 100) {
      console.warn(`⚠️ Slow ${operation}: ${duration.toFixed(2)}ms`);
    }
  }

  static getAverageDuration(operation: string): number {
    const measurements = this.measurements.get(operation);
    if (!measurements || measurements.length === 0) return 0;

    const sum = measurements.reduce((acc, duration) => acc + duration, 0);
    return sum / measurements.length;
  }

  static getAllStats(): Record<string, { avg: number; count: number; last: number }> {
    const stats: Record<string, { avg: number; count: number; last: number }> = {};

    this.measurements.forEach((measurements, operation) => {
      if (measurements.length > 0) {
        const sum = measurements.reduce((acc, duration) => acc + duration, 0);
        stats[operation] = {
          avg: sum / measurements.length,
          count: measurements.length,
          last: measurements[measurements.length - 1],
        };
      }
    });

    return stats;
  }
}

export class CacheCleanupService {
  private static instance: CacheCleanupService | null = null;
  private cache: InMemoryCache;
  private ttlStore: Map<string, number>;
  private config: CacheCleanupConfig;
  private intervalId: number | null = null;
  private isRunning = false;
  private stats: CacheHealthStats = {
    cacheSize: 0,
    totalCleaned: 0,
    lastCleanupDuration: 0,
    scheduledCleanups: 0,
    skippedCleanups: 0,
    isRunning: false,
    avgCleanupDuration: 0,
  };
  private listeners = new Set<() => void>();
  private windowUnloadHandler: (() => void) | null = null;

  private constructor(cache: InMemoryCache, ttlStore: Map<string, number>, config: CacheCleanupConfig) {
    this.cache = cache;
    this.ttlStore = ttlStore;
    this.config = { ...config };
    this.updateCacheSize();

    // Add cleanup on window unload to prevent memory leaks
    this.windowUnloadHandler = () => this.cleanup();
    window.addEventListener('beforeunload', this.windowUnloadHandler);
  }

  static getInstance(
    cache: InMemoryCache,
    ttlStore: Map<string, number>,
    config: CacheCleanupConfig
  ): CacheCleanupService {
    if (!CacheCleanupService.instance) {
      CacheCleanupService.instance = new CacheCleanupService(cache, ttlStore, config);
    }
    return CacheCleanupService.instance;
  }

  start(): void {
    if (this.intervalId || !this.config.enabled) return;

    //console.log('🚀 Starting cache cleanup service with config:', this.config);

    this.intervalId = window.setInterval(() => {
      this.scheduleCleanup();
    }, this.config.intervalMs);

    // Initial cleanup after a short delay
    setTimeout(() => this.scheduleCleanup(), 5000);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('🛑 Cache cleanup service stopped');
    }
  }

  updateConfig(newConfig: Partial<CacheCleanupConfig>): void {
    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...newConfig };

    if (process.env.NODE_ENV === 'development') {
      console.log('⚙️ Cache cleanup config updated:', {
        old: oldConfig,
        new: this.config,
      });
    }

    // Restart with new interval if it changed
    if (newConfig.intervalMs && newConfig.intervalMs !== oldConfig.intervalMs) {
      this.stop();
      if (this.config.enabled) {
        this.start();
      }
    }
  }

  async forceCleanup(): Promise<void> {
    await this.performCleanup();
  }

  getStats(): CacheHealthStats {
    this.updateCacheSize();
    return { ...this.stats };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private scheduleCleanup(): void {
    if (!this.config.enabled) return;

    this.stats.scheduledCleanups++;

    if (this.isRunning) {
      this.stats.skippedCleanups++;
      console.log('⏭️ Skipping cleanup - already running');
      return;
    }

    // Use different scheduling strategies based on availability
    if (typeof requestIdleCallback !== 'undefined') {
      // Lowest priority - run when browser is idle
      requestIdleCallback(() => this.performCleanup(), { timeout: 5000 });
    } else if (typeof requestAnimationFrame !== 'undefined') {
      // Run before next frame
      requestAnimationFrame(() => this.performCleanup());
    } else {
      // Fallback to setTimeout
      setTimeout(() => this.performCleanup(), 0);
    }
  }

  private async performCleanup(): Promise<void> {
    if (!this.config.enabled || this.isRunning) return;

    this.isRunning = true;
    this.stats.isRunning = true;
    this.notifyListeners();

    const startTime = performance.now();
    let cleanedCount = 0;

    try {
      await PerformanceMonitor.measureAsync('cache-cleanup', async () => {
        const expiredEntries = this.findExpiredEntries();

        if (expiredEntries.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            //console.log('✨ Cache cleanup: No expired entries found');
          }
          return;
        }

        //console.log(`🧹 Cache cleanup: Found ${expiredEntries.length} expired entries`);

        // Process in batches with throttling
        for (let i = 0; i < expiredEntries.length; i += this.config.batchSize) {
          const batch = expiredEntries.slice(i, i + this.config.batchSize);

          await this.processBatch(batch);
          cleanedCount += batch.length;

          // Check if we've exceeded max cleanup time
          if (performance.now() - startTime > this.config.maxCleanupTime) {
            //console.log(`⏰ Cache cleanup: Time limit reached (${this.config.maxCleanupTime}ms)`);
            break;
          }

          // Throttle between batches
          if (i + this.config.batchSize < expiredEntries.length) {
            await this.sleep(this.config.throttleMs);
          }
        }

        // Evict from cache
        if (cleanedCount > 0) {
          this.evictFromCache(expiredEntries.slice(0, cleanedCount));
        }
      });
    } catch (error) {
      console.error('❌ Cache cleanup error:', error);
    } finally {
      const duration = performance.now() - startTime;

      this.stats.totalCleaned += cleanedCount;
      this.stats.lastCleanupDuration = duration;
      this.stats.avgCleanupDuration = PerformanceMonitor.getAverageDuration('cache-cleanup');
      this.stats.isRunning = false;
      this.isRunning = false;

      this.updateCacheSize();
      this.notifyListeners();

      if (cleanedCount > 0) {
        //console.log(`✅ Cache cleanup complete: ${cleanedCount} entries cleaned in ${duration.toFixed(1)}ms`);
      }

      // Adaptive configuration based on performance
      this.adaptConfiguration(duration, cleanedCount);
    }
  }

  private findExpiredEntries(): string[] {
    const now = Date.now();
    const expired: string[] = [];

    for (const [cacheId, expiry] of this.ttlStore.entries()) {
      if (now > expiry) {
        expired.push(cacheId);
      }
    }

    return expired;
  }

  private async processBatch(batch: string[]): Promise<void> {
    return new Promise(resolve => {
      // Process batch in next tick to avoid blocking
      setTimeout(() => {
        batch.forEach(cacheId => {
          this.ttlStore.delete(cacheId);
        });
        resolve();
      }, 0);
    });
  }

  private evictFromCache(expiredEntries: string[]): void {
    let evictedCount = 0;

    expiredEntries.forEach(cacheId => {
      try {
        // cacheId is already in Apollo's format like "Move:123" or "Driver:456"
        const result = this.cache.evict({ id: cacheId });
        if (result) {
          evictedCount++;
        }
      } catch (error) {
        console.warn(`⚠️ Failed to evict cache entry ${cacheId}:`, error);
      }
    });

    // Garbage collect to free memory after evictions
    if (evictedCount > 0) {
      this.cache.gc();
      //console.log(`🗑️ Evicted ${evictedCount} cache entries and ran garbage collection`);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private updateCacheSize(): void {
    try {
      // Get cache size by extracting data
      const cacheData = this.cache.extract();
      this.stats.cacheSize = Object.keys(cacheData).length;
    } catch (error) {
      console.warn('⚠️ Failed to get cache size:', error);
      this.stats.cacheSize = this.ttlStore.size; // Fallback
    }
  }

  private adaptConfiguration(duration: number, cleanedCount: number): void {
    // Adaptive configuration based on performance
    if (duration > this.config.maxCleanupTime * 0.8) {
      // Cleanup is taking too long - reduce batch size
      const newBatchSize = Math.max(10, Math.floor(this.config.batchSize * 0.8));
      if (newBatchSize !== this.config.batchSize) {
        //console.log(`📉 Reducing batch size: ${this.config.batchSize} → ${newBatchSize}`);
        this.config.batchSize = newBatchSize;
      }
    } else if (duration < this.config.maxCleanupTime * 0.3 && cleanedCount > 0) {
      // Cleanup is very fast - can increase batch size
      const newBatchSize = Math.min(100, Math.floor(this.config.batchSize * 1.2));
      if (newBatchSize !== this.config.batchSize) {
        //console.log(`📈 Increasing batch size: ${this.config.batchSize} → ${newBatchSize}`);
        this.config.batchSize = newBatchSize;
      }
    }

    // Adjust interval based on cache size
    const targetInterval =
      this.stats.cacheSize > 1000
        ? Math.max(60000, this.config.intervalMs * 0.8) // More frequent for large caches
        : Math.min(600000, this.config.intervalMs * 1.2); // Less frequent for small caches

    if (Math.abs(targetInterval - this.config.intervalMs) > 30000) {
      //console.log(`🔄 Adjusting cleanup interval: ${this.config.intervalMs}ms → ${targetInterval}ms`);
      this.updateConfig({ intervalMs: targetInterval });
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('❌ Error in cache health listener:', error);
      }
    });
  }

  // Clean up all resources to prevent memory leaks
  private cleanup(): void {
    this.stop();
    this.listeners.clear();

    if (this.windowUnloadHandler) {
      window.removeEventListener('beforeunload', this.windowUnloadHandler);
      this.windowUnloadHandler = null;
    }
  }

  // Clear all Apollo cache data (for environment switching)
  clearAllCache(): void {
    //console.log('🧹 Clearing entire Apollo cache...');

    try {
      // Clear the Apollo cache completely
      // Use reset() to clear all cache data and normalize it
      this.cache.reset();

      // Clear TTL store
      this.ttlStore.clear();

      // Reset stats
      this.stats.totalCleaned = 0;
      this.stats.lastCleanupDuration = 0;
      this.stats.avgCleanupDuration = 0;

      // Update cache size
      this.updateCacheSize();

      // Notify listeners
      this.notifyListeners();

      //console.log('✅ Apollo cache cleared successfully');
    } catch (error) {
      console.error('❌ Failed to clear Apollo cache:', error);
      throw error;
    }
  }

  // Reset cache without triggering network requests (useful for environment switching)
  resetCache(): void {
    //console.log('🔄 Resetting Apollo cache...');

    try {
      // Reset the Apollo cache (clears cache but doesn't refetch)
      this.cache.reset();

      // Clear TTL store
      this.ttlStore.clear();

      // Reset stats
      this.stats.totalCleaned = 0;
      this.stats.lastCleanupDuration = 0;
      this.stats.avgCleanupDuration = 0;

      // Update cache size
      this.updateCacheSize();

      // Notify listeners
      this.notifyListeners();

      //console.log('✅ Apollo cache reset successfully');
    } catch (error) {
      console.error('❌ Failed to reset Apollo cache:', error);
      throw error;
    }
  }

  // Clear cache for specific entity types (useful for targeted clearing)
  clearCacheByType(typenames: string[]): void {
    //console.log(`🎯 Clearing cache for types: ${typenames.join(', ')}`);

    let clearedCount = 0;

    try {
      // Find all cache entries for the specified types
      const cacheData = this.cache.extract();
      const keysToEvict: string[] = [];

      Object.keys(cacheData).forEach(key => {
        typenames.forEach(typename => {
          if (key.startsWith(`${typename}:`)) {
            keysToEvict.push(key);
          }
        });
      });

      // Evict the found keys
      keysToEvict.forEach(key => {
        const result = this.cache.evict({ id: key });
        if (result) {
          clearedCount++;
          this.ttlStore.delete(key);
        }
      });

      // Garbage collect
      if (clearedCount > 0) {
        this.cache.gc();
      }

      // Update stats
      this.stats.totalCleaned += clearedCount;
      this.updateCacheSize();
      this.notifyListeners();

      //console.log(`✅ Cleared ${clearedCount} cache entries for types: ${typenames.join(', ')}`);
    } catch (error) {
      console.error('❌ Failed to clear cache by type:', error);
      throw error;
    }
  }

  // Public method to destroy the singleton instance
  static destroy(): void {
    if (CacheCleanupService.instance) {
      CacheCleanupService.instance.cleanup();
      CacheCleanupService.instance = null;
    }
  }
}

// Note: useCacheHealth hook moved to separate file to avoid circular dependencies